/**
 * R232q5i — GM admin panel.  Single owner: 03021992caramell@gmail.com.
 *
 * Opens with Ctrl+Shift+G (only if isGm).  Tabs: Self / Target / World /
 * Players.  All actions go through Colyseus `gm-action` message; server
 * validates GM via aiwari_gm_account email lookup.
 */
import { useState, useEffect, useCallback } from 'react';
import { getRoom } from '../game/realmClient.js';

type Tab = 'self' | 'target' | 'world' | 'players';

interface GmCheckResult { isGm: boolean; isOwner: boolean; email: string | null }

export function GMPanel(): JSX.Element | null {
  const room = getRoom();
  const [open, setOpen] = useState(false);
  const [check, setCheck] = useState<GmCheckResult>({ isGm: false, isOwner: false, email: null });
  const [tab, setTab] = useState<Tab>('self');
  const [target, setTarget] = useState('');
  const [feedback, setFeedback] = useState<string>('');
  const [itemId, setItemId] = useState('');
  const [itemCount, setItemCount] = useState(1);
  const [gold, setGold] = useState(10000);
  const [level, setLevel] = useState(80);
  const [hp, setHp] = useState(99999);
  const [mana, setMana] = useState(99999);
  const [spellId, setSpellId] = useState('');
  const [tpZone, setTpZone] = useState('');
  const [tpX, setTpX] = useState(0);
  const [tpY, setTpY] = useState(0);
  const [tpZ, setTpZ] = useState(0);
  const [announce, setAnnounce] = useState('');
  const [speedMul, setSpeedMul] = useState(2);

  // GM-check on room ready.
  useEffect(() => {
    if (!room) return;
    room.send('gm-check', {});
    const off = room.onMessage('gm-check-result', (m: GmCheckResult) => setCheck(m));
    return () => off();
  }, [room]);

  // Action result toaster.
  useEffect(() => {
    if (!room) return;
    const off = room.onMessage('gm-action-result', (m: { action: string; ok: boolean; error?: string; info?: string }) => {
      if (m.ok) setFeedback(`✓ ${m.action}: ${m.info ?? 'ok'}`);
      else setFeedback(`✗ ${m.action}: ${m.error ?? 'failed'}`);
      window.setTimeout(() => setFeedback(''), 4000);
    });
    return () => off();
  }, [room]);

  // Hotkey: Ctrl+Shift+G to toggle.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!check.isGm) return;
      if (e.ctrlKey && e.shiftKey && (e.key === 'G' || e.key === 'g' || e.code === 'KeyG')) {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === 'Escape' && open) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [check.isGm, open]);

  const send = useCallback((action: string, payload: Record<string, unknown> = {}) => {
    if (!room) return;
    room.send('gm-action', { action, payload, target: target || undefined });
  }, [room, target]);

  if (!check.isGm || !open) return null;

  const tabBtn = (id: Tab, label: string): JSX.Element => (
    <button
      onClick={() => setTab(id)}
      style={{
        padding: '8px 18px',
        background: tab === id ? '#3A2818' : '#161210',
        color: tab === id ? '#F2D58A' : '#9C8E72',
        border: '1px solid ' + (tab === id ? '#C9A96A' : '#3A2818'),
        cursor: 'pointer',
        fontFamily: '"Spectral SC", serif',
        fontSize: 13,
        textTransform: 'uppercase',
        letterSpacing: 1,
      }}>{label}</button>
  );

  const fieldRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 };
  const labelStyle: React.CSSProperties = { width: 110, fontSize: 13, color: '#9C8E72', fontFamily: '"EB Garamond", serif' };
  const inputStyle: React.CSSProperties = {
    background: '#0B0907', border: '1px solid #3A2818', color: '#E8DCC0',
    padding: '4px 10px', fontFamily: '"JetBrains Mono", monospace', fontSize: 13,
    flex: 1, outline: 'none',
  };
  const btnStyle: React.CSSProperties = {
    background: 'linear-gradient(180deg, #3A2818, #1F1813)',
    border: '1px solid #C9A96A',
    color: '#F2D58A',
    padding: '6px 14px', fontSize: 12, cursor: 'pointer',
    fontFamily: '"Spectral SC", serif', textTransform: 'uppercase', letterSpacing: 1,
  };

  return (
    <div style={{
      position: 'fixed', top: 60, left: '50%', transform: 'translateX(-50%)',
      width: 520, maxHeight: '85vh', overflowY: 'auto',
      background: 'linear-gradient(180deg, #161210, #0B0907)',
      border: '2px solid #C9A96A',
      boxShadow: '0 0 40px rgba(201,169,106,0.3), inset 0 0 20px rgba(0,0,0,0.5)',
      padding: 18, zIndex: 9999,
      fontFamily: '"EB Garamond", serif',
      color: '#E8DCC0',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, borderBottom: '1px solid #5C4520', paddingBottom: 10 }}>
        <div style={{ fontFamily: '"Cinzel", serif', fontSize: 22, color: '#F2D58A', letterSpacing: 2 }}>⚜ ПАНЕЛЬ GM</div>
        <div style={{ fontSize: 11, color: '#9C8E72' }}>{check.email}{check.isOwner ? ' · OWNER' : ''}</div>
        <button onClick={() => setOpen(false)} style={{ background: 'transparent', border: '1px solid #5C4520', color: '#9C8E72', cursor: 'pointer', padding: '4px 10px' }}>✕</button>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 14 }}>
        {tabBtn('self', 'Себе')}
        {tabBtn('target', 'Цель')}
        {tabBtn('world', 'Мир')}
        {tabBtn('players', 'Игроки')}
      </div>

      {tab === 'target' && (
        <div style={fieldRow}>
          <span style={labelStyle}>Имя цели:</span>
          <input style={inputStyle} value={target} onChange={(e) => setTarget(e.target.value)} placeholder="оставь пустым = себе" />
        </div>
      )}

      {(tab === 'self' || tab === 'target') && (
        <>
          <h3 style={{ fontFamily: '"Spectral SC", serif', color: '#C9A96A', fontSize: 14, marginTop: 14, letterSpacing: 1 }}>ВЫДАЧА ПРЕДМЕТА</h3>
          <div style={fieldRow}>
            <span style={labelStyle}>ID предмета:</span>
            <input style={inputStyle} value={itemId} onChange={(e) => setItemId(e.target.value)} placeholder="напр. stormblade" />
          </div>
          <div style={fieldRow}>
            <span style={labelStyle}>Количество:</span>
            <input style={{ ...inputStyle, flex: 0, width: 80 }} type="number" value={itemCount} onChange={(e) => setItemCount(Number(e.target.value))} />
            <button style={btnStyle} onClick={() => send('give-item', { itemId, count: itemCount })}>Выдать</button>
          </div>

          <h3 style={{ fontFamily: '"Spectral SC", serif', color: '#C9A96A', fontSize: 14, marginTop: 14, letterSpacing: 1 }}>СТАТЫ</h3>
          <div style={fieldRow}>
            <span style={labelStyle}>Золото:</span>
            <input style={{ ...inputStyle, flex: 0, width: 130 }} type="number" value={gold} onChange={(e) => setGold(Number(e.target.value))} />
            <button style={btnStyle} onClick={() => send('set-gold', { amount: gold })}>Установить</button>
          </div>
          <div style={fieldRow}>
            <span style={labelStyle}>Уровень:</span>
            <input style={{ ...inputStyle, flex: 0, width: 80 }} type="number" min={1} max={80} value={level} onChange={(e) => setLevel(Number(e.target.value))} />
            <button style={btnStyle} onClick={() => send('set-level', { level })}>Установить</button>
          </div>
          <div style={fieldRow}>
            <span style={labelStyle}>HP:</span>
            <input style={{ ...inputStyle, flex: 0, width: 130 }} type="number" value={hp} onChange={(e) => setHp(Number(e.target.value))} />
            <button style={btnStyle} onClick={() => send('set-hp', { hp })}>Установить</button>
          </div>
          <div style={fieldRow}>
            <span style={labelStyle}>Мана:</span>
            <input style={{ ...inputStyle, flex: 0, width: 130 }} type="number" value={mana} onChange={(e) => setMana(Number(e.target.value))} />
            <button style={btnStyle} onClick={() => send('set-mana', { mana })}>Установить</button>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <button style={btnStyle} onClick={() => send('heal-full', {})}>Полное лечение</button>
            <button style={btnStyle} onClick={() => send('godmode', { on: true })}>Бессмертие ВКЛ</button>
            <button style={btnStyle} onClick={() => send('godmode', { on: false })}>ВЫКЛ</button>
          </div>

          <h3 style={{ fontFamily: '"Spectral SC", serif', color: '#C9A96A', fontSize: 14, marginTop: 14, letterSpacing: 1 }}>СПОСОБНОСТИ</h3>
          <div style={fieldRow}>
            <span style={labelStyle}>ID способности:</span>
            <input style={inputStyle} value={spellId} onChange={(e) => setSpellId(e.target.value)} placeholder="напр. fireball-r5" />
            <button style={btnStyle} onClick={() => send('learn-spell', { spellId })}>Изучить</button>
          </div>

          <h3 style={{ fontFamily: '"Spectral SC", serif', color: '#C9A96A', fontSize: 14, marginTop: 14, letterSpacing: 1 }}>СКОРОСТЬ / НЕВИДИМОСТЬ</h3>
          <div style={fieldRow}>
            <span style={labelStyle}>Скорость ×:</span>
            <input style={{ ...inputStyle, flex: 0, width: 80 }} type="number" step={0.5} min={0.1} max={10} value={speedMul} onChange={(e) => setSpeedMul(Number(e.target.value))} />
            <button style={btnStyle} onClick={() => send('speed', { mul: speedMul })}>Применить</button>
            <button style={btnStyle} onClick={() => send('speed', { mul: 1 })}>×1</button>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={btnStyle} onClick={() => send('invisible', { on: true })}>Невидимость ВКЛ</button>
            <button style={btnStyle} onClick={() => send('invisible', { on: false })}>ВЫКЛ</button>
          </div>
        </>
      )}

      {tab === 'world' && (
        <>
          <h3 style={{ fontFamily: '"Spectral SC", serif', color: '#C9A96A', fontSize: 14, letterSpacing: 1 }}>ТЕЛЕПОРТ</h3>
          <div style={fieldRow}>
            <span style={labelStyle}>Зона:</span>
            <input style={inputStyle} value={tpZone} onChange={(e) => setTpZone(e.target.value)} placeholder="elwynn / durotar / stormwind / ..." />
          </div>
          <div style={fieldRow}>
            <span style={labelStyle}>X / Y / Z:</span>
            <input style={{ ...inputStyle, flex: 0, width: 80 }} type="number" value={tpX} onChange={(e) => setTpX(Number(e.target.value))} placeholder="X" />
            <input style={{ ...inputStyle, flex: 0, width: 80 }} type="number" value={tpY} onChange={(e) => setTpY(Number(e.target.value))} placeholder="Y" />
            <input style={{ ...inputStyle, flex: 0, width: 80 }} type="number" value={tpZ} onChange={(e) => setTpZ(Number(e.target.value))} placeholder="Z" />
            <button style={btnStyle} onClick={() => send('teleport', { zone: tpZone, x: tpX, y: tpY, z: tpZ })}>Лететь</button>
          </div>

          <h3 style={{ fontFamily: '"Spectral SC", serif', color: '#C9A96A', fontSize: 14, marginTop: 14, letterSpacing: 1 }}>ОБЪЯВЛЕНИЕ</h3>
          <div style={fieldRow}>
            <input style={inputStyle} value={announce} onChange={(e) => setAnnounce(e.target.value)} placeholder="Текст для всех на сервере…" />
            <button style={btnStyle} onClick={() => { send('announce', { text: announce }); setAnnounce(''); }}>📢</button>
          </div>
        </>
      )}

      {tab === 'players' && (
        <>
          <h3 style={{ fontFamily: '"Spectral SC", serif', color: '#C9A96A', fontSize: 14, letterSpacing: 1 }}>ИГРОК</h3>
          <div style={fieldRow}>
            <span style={labelStyle}>Имя:</span>
            <input style={inputStyle} value={target} onChange={(e) => setTarget(e.target.value)} placeholder="имя цели" />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={btnStyle} onClick={() => send('kill-target', {})}>Убить</button>
            <button style={btnStyle} onClick={() => send('kick-player', {})}>Кикнуть</button>
            <button style={btnStyle} onClick={() => send('heal-full', {})}>Вылечить</button>
            <button style={btnStyle} onClick={() => send('teleport', { zone: 'goldshire', x: 0, y: 0, z: 0 })}>Призвать</button>
          </div>
        </>
      )}

      {feedback && (
        <div style={{
          marginTop: 16, padding: '8px 12px',
          background: feedback.startsWith('✓') ? '#1A2A1A' : '#2A1A1A',
          borderLeft: '3px solid ' + (feedback.startsWith('✓') ? '#5BAE57' : '#A33232'),
          fontFamily: '"JetBrains Mono", monospace', fontSize: 12,
          color: '#E8DCC0',
        }}>{feedback}</div>
      )}

      <div style={{ marginTop: 14, fontSize: 11, color: '#5C4520', textAlign: 'center' }}>
        Ctrl+Shift+G — открыть/закрыть · Esc — закрыть
      </div>
    </div>
  );
}
